import { useEffect, useState } from "react";
import { formatPriceNumber, fromM2, toM2 } from "../function";
import { AreaUnit } from "../machine";

interface Options {
  baseValue: number;
  unit?: AreaUnit;
  min?: number;
  max?: number;
  isPrice?: boolean;
  onChangeBaseValue?: (v: number) => void;
}

export function useFormattedNumberField({
  baseValue,
  unit,
  min,
  max,
  onChangeBaseValue,
  isPrice = true,
}: Options) {
  const [display, setDisplay] = useState("0");

  // baseValue / unit 변경 시 display 갱신
  useEffect(() => {
    if (unit) {
      const unitValue = fromM2(baseValue, unit);
      // setDisplay(formatPriceNumber(unitValue));
      const formatted = Number(unitValue.toFixed(2)).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });

      setDisplay(formatted);
    } else if (isPrice) {
      setDisplay(formatPriceNumber(baseValue));
    } else {
      setDisplay(baseValue.toLocaleString());
    }
  }, [baseValue, unit]);

  const handleChange = (raw: string) => {
    let cleaned = raw.replace(/,/g, ""); // 콤마 제거
    cleaned = cleaned.replace(/[^0-9.]/g, ""); // 숫자 + .만 허용

    const parts = cleaned.split(".");
    if (parts.length > 2) {
      cleaned = parts[0] + "." + parts.slice(1).join("");
    }

    setDisplay(cleaned);
  };

  const handleBlur = () => {
    if (!display) {
      const fallback = min ?? 0;
      onChangeBaseValue?.(fallback);
      setDisplay(String(fallback));
      return;
    }

    let num = Number(display);
    if (isNaN(num)) return;

    if (min !== undefined) num = Math.max(num, min);
    if (max !== undefined) num = Math.min(num, max);

    const base = unit ? toM2(num, unit) : num;
    onChangeBaseValue?.(base);

    // 유닛 기준 값으로 다시 포맷
    if (unit) {
      const unitValue = fromM2(base, unit);
      setDisplay(formatPriceNumber(unitValue));
    } else if (isPrice) {
      setDisplay(formatPriceNumber(base));
    } else {
      setDisplay(base.toLocaleString());
    }
  };

  return {
    display,
    setDisplay,
    handleChange,
    handleBlur,
  };
}
